import { AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import { PartMatchingResponse } from 'src/api/lists/interfaces'
import {
  LISTS_IMPORT_RETRIES_INTERVAL,
  LISTS_IMPORT_RETRIES_LIMIT,
} from 'src/config/constants'
import { CSVDownloadFile } from 'src/features/lists/components/RightPanel/ListGroupsHeader'
import miscellaneousVehicle from 'src/features/search/Results/utils/miscellaneousVehicle'
import history from 'src/helpers/history'
import ListServiceProvider, {
  ListImportResponse,
} from 'src/services/ListServiceProvider'
import PartExtServiceProvider from 'src/services/PartExtServiceProvider'
import { breakpoints } from 'src/theme/breakpoints'
import { StoreInstances } from '../StoreInstancesContainer'
import { showCartPartComparisionNotification } from '../cart/Utils'
import { IdNamePair, IdValueGeneric, IdValuePair } from '../models/KeyValuePair'
import { ProductLocationModel } from '../models/ProductModel'
import { SortOrder } from '../models/SearchModels'
import { Vehicle } from '../models/Vehicles'
import ListNode from './ListNode'
import { getValidatedPartsList } from './Utils'
import {
  AddGroupPart,
  AddPartToList,
  CheckedListParts,
  DeleteGroupParts,
  GroupPart,
  GroupPartResponse,
  ListImportDetails,
  ListResponseError,
  MoveGroupParts,
  Product,
  RequestBodyNewList,
  UpdateGroupPart,
  ValidateListPart,
  ValidatePartsRequest,
} from './interfaces'
import { SELLER_MANAGED_LIST_LABEL } from '../../features/lists/components/interfaces'
import { GroupSelectionType } from 'src/features/lists/components/RightPanel/interfaces'
import { categoriestoExclude } from 'src/features/search/AllProductsSearch/constants'
import { ImageBypartAPIResponse } from 'src/api/searchResults/interfaces'

interface SelectedList {
  id: number
  label: string
}

interface SelectedCategory {
  id: number
  label: string
}

interface NameSelectedList {
  id?: number
  label?: string
  category: string
}

export const DEFAULT_SORT_ORDER: SortOrder = [
  {
    property: 'Mfr',
    label: 'Mfr (A-Z)',
    dir: 'asc',
  },
  {
    property: 'PartNumber',
    label: 'Part Number (A-Z)',
    dir: 'asc',
  },
]
interface IUpdateGroupPartPaylod {
  stockQty?: number
}

export enum ImportStatus {
  STARTED = 'started',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export interface BasketGroup {
  id: number
  parts: GroupPart[]
}

export interface BasketItem {
  category: IdValueGeneric<number, string>
  listId: number
  groups: BasketGroup[]
}

export interface SelectedPartsBasket {
  items: BasketItem[]
}

export class ListsStore {
  public categoryNameField = ''

  public categoryNameModal = false

  public categoryNameModalType: 'new' | 'rename'

  public categoryToRename: IdValuePair<string>

  public deleteCategoryModal = false

  public deleteListModal = false

  public deleteSelectedCategory: SelectedCategory

  public deleteSelectedList: SelectedList

  public deleteSelectedGroupPart: SelectedList

  public listNameModalType: 'new' | 'rename'

  public showListGroups = false

  public loadingCategories = false

  public loadingGroups = false

  public moveListModal = false

  public matchingFilteredSearchTerms: PartMatchingResponse = []

  public nameListModal = false

  public nameListNameField = ''

  public nameListNotesField = ''

  public nameSelectedList: NameSelectedList

  public selectedList: ListNode = undefined

  public selectedCategory: ListNode = undefined

  public selectedGroup: ListNode = undefined

  public showBottomDrawer = false

  public showDeleteItemsFromListsModal = false

  public checkedListParts: Array<CheckedListParts> = []

  public changeLocationParts: Array<ProductLocationModel> = []

  public cartSelectorModal = false

  public root: ListNode = new ListNode(0, 'root')

  public listImportRespData: ListImportResponse

  public selectedVehicle: Vehicle = miscellaneousVehicle

  public selectionOption: GroupSelectionType = new Map()

  public importListPartsLoading = false

  public newListCategoryId = undefined

  public showAddEditGroupModal = false

  public selectedListGroups = []

  public selectedGroupValue = 0

  /**
   *  selectedGroupParts property refers to the parts that are fetched from API for a given list and group
   */
  public selectedGroupParts: Array<GroupPart> = []

  public selectedGroupPartsCount = 0

  public groupPartsLoading = false

  public searchTerm = undefined

  public moveListGroups: Array<IdNamePair<number, string>> = []

  public thumbnails: ImageBypartAPIResponse[] = []

  public currentPage = 0

  public itemsPerPage = 10

  public sortOrder: SortOrder = DEFAULT_SORT_ORDER

  // public enableSortIcons = ''

  public listImportDetails: ListImportDetails = undefined

  onCategoryDeleted = false

  onListDeleted = false

  public arePartsBeingAddedToCart = false

  private _retryInterval = 0
  private _retryLimit = 0

  private _printData: CSVDownloadFile[] = []

  private _selectedPartsBasket: SelectedPartsBasket = null
  public multipleMoveOrDeleteParts = []

  constructor() {
    makeAutoObservable(this)
    this.initializeListStoreFromUIStore()
    this.initRetryConfig()
    this.initializeSortOrder()
  }

  private initRetryConfig() {
    const interval = Number(LISTS_IMPORT_RETRIES_INTERVAL)
    const limit = Number(LISTS_IMPORT_RETRIES_LIMIT)

    this._retryLimit = limit / interval // How many retries can be made in the given time period
    this._retryInterval = interval
  }

  public initializeListStoreFromUIStore = (): void => {
    //selectedList - used to store the selected list from the category
    this.selectedList = StoreInstances.uiStore.listStoreProps.selectedList

    //selectedGroupValue - contains by default 0 and value will be updated based on the selected group index
    this.selectedGroupValue =
      StoreInstances.uiStore.listStoreProps.selectedGroupValue

    //selectedGroup - constains the selected group value
    this.selectedGroup = StoreInstances.uiStore.listStoreProps.selectedGroup

    //selectedListGroups - contains all the groups of the selected list.
    this.selectedListGroups =
      StoreInstances.uiStore.listStoreProps.selectedListGroups

    //selectedCategories - contains all the categories based on selected category.
    this.selectedCategory =
      StoreInstances.uiStore.listStoreProps.selectedCategory
  }

  public initializeSortOrder = (): void => {
    const { v3listSortOrder } = StoreInstances.userStore.preferences
    const defaultSortValue =
      v3listSortOrder && v3listSortOrder !== 'null'
        ? v3listSortOrder
        : 'Mfr_0,PartNumber_0'

    const sortDefaultOptions = defaultSortValue
      .split(',')
      .map((it) => it.split('_'))

    if (sortDefaultOptions?.length > 0) {
      const sortPartial = []

      const sortBy = {
        property: sortDefaultOptions[0][0],
        dir: sortDefaultOptions[0][1] === '0' ? 'asc' : 'desc',
      }

      const thenBy = {
        property: sortDefaultOptions?.[1]?.[0],
        dir: sortDefaultOptions?.[1]?.[1] ? 'asc' : 'desc',
      }
      sortPartial.push(sortBy)
      sortPartial.push(sortDefaultOptions?.[1]?.[0] ? thenBy : '')
      this.sortOrder = sortPartial
    }
  }

  public getPrintData(): CSVDownloadFile[] {
    return this._printData
  }

  public handlePaginationLimit = (start: number, limit: number): void => {
    this.currentPage = start
    this.itemsPerPage = limit
  }

  setSortOrder = (sortOrder: SortOrder): void => {
    this.sortOrder = sortOrder
    this.getListGroupParts()
  }

  public handleCloseCartSelectorModal = (): void => {
    this.cartSelectorModal = false
  }

  public setThumbnails = (thumbnails: Array<ImageBypartAPIResponse>): void => {
    this.thumbnails = thumbnails
  }

  public getThumbnailUrl = (partNumber: string, lineCode: string): string => {
    const thumbnailUrl = this.thumbnails?.find(
      (item) =>
        item?.partNumber === partNumber &&
        item?.linecode?.toUpperCase() === lineCode?.toUpperCase()
    )

    if (thumbnailUrl) {
      return thumbnailUrl?.partImages?.[0]?.url
    }
    return ''
  }

  public fetchPartAttributes = async (
    parts: GroupPart[]
  ): Promise<ImageBypartAPIResponse[]> => {
    if (!parts?.length || parts.length <= 0) return []

    const requestBody = parts.map((p) => ({
      partNumber: p.partNumber,
      lineCode: p.lineCode,
    }))

    const attributes =
      await PartExtServiceProvider.getPartAttributesByPart(requestBody)

    return attributes
  }

  public fillPartAttributes = async (parts: GroupPart[]): Promise<void> => {
    const attributes = await this.fetchPartAttributes(parts)
    if (attributes?.length > 0) this.setThumbnails(attributes)
  }

  public getGroupSlice(start: number, end: number): GroupPart[] {
    if (!this.selectedGroupParts?.length) return []

    const parts = this.selectedGroupParts?.slice(start, end)
    this.fillPartAttributes(parts)

    return parts
  }

  public setSelectionOption = (selectionOption: GroupSelectionType): void => {
    this.selectionOption = selectionOption
  }

  public setShowListGroups = (show: boolean): void => {
    this.showListGroups = show
  }

  public search = async (searchTerm: string): Promise<void> => {
    if (searchTerm === '') {
      this.resetListSearchResults()
      this.getCategories()
    } else {
      this.searchTerm = searchTerm
      this.loadingCategories = true
      this.matchingFilteredSearchTerms =
        await ListServiceProvider.getMatchingParts(searchTerm)
      this.handleListCategories()
    }
  }

  public resetListSearchResults = (): void => {
    this.searchTerm = undefined
    this.matchingFilteredSearchTerms = []
  }

  private handleListCategories = (): void => {
    this.root.deleteAllChildren()
    this.matchingFilteredSearchTerms.forEach((list) => {
      let matchingCategoryNode = this.root.findChildren(
        list.category.id,
        list.category.name
      )
      if (!matchingCategoryNode) {
        matchingCategoryNode = new ListNode(
          list.category.id,
          list.category.name,
          'category'
        )
        this.root.addChild(matchingCategoryNode)
      }
      const listNode = new ListNode(list.id, list.name, 'list')
      this.root
        .findChildren(list.category.id, list.category.name)
        .addChild(listNode)
    })

    this.loadingCategories = false
  }

  public setCartSelectorModal = (set: boolean): void => {
    this.cartSelectorModal = set
  }

  public setSelectedVehicle = (vehicle: Vehicle): void => {
    this.selectedVehicle = vehicle
  }

  public setShowBottomDrawer = (modal: boolean): void => {
    this.showBottomDrawer = modal
  }

  public setShowDeleteItemsFromListsModal = (modal: boolean): void => {
    this.showDeleteItemsFromListsModal = modal
  }

  public setDeleteListModal = (modal: boolean): void => {
    this.deleteListModal = modal
  }

  public setDeleteCategoryModal = (modal: boolean): void => {
    this.deleteCategoryModal = modal
  }

  public setNameListNameField = (nameString: string): void => {
    this.nameListNameField = nameString
  }

  public setCategoryNameModal = (modal: boolean): void => {
    if (!modal) {
      this.categoryNameField = ''
      this.categoryToRename = null
    }
    this.categoryNameModal = modal
  }

  public setNameListNotesField = (notes: string): void => {
    this.nameListNotesField = notes
  }

  public setCategoryNameModalType = (type: 'new' | 'rename'): void => {
    this.categoryNameModalType = type
  }

  public setListNameModalType = (type: 'new' | 'rename'): void => {
    this.listNameModalType = type
    if (type === 'new') {
      this.nameListNameField = ''
      this.nameListNotesField = ''

      /*  In below step is necessary because, when we create a new list, we are resetting the fields to empty. So we are overriding
      previosly selected list's values in nameListNameField, nameListNameField with empty strings. To get those values in these properties we need
      to reselect the list. Then only a fresh API call(getListDetails) will happen and these values will be set.

      Otherwise we should make a fresh API call(getListDetails) for the selectedList to restore the nameListNameField & nameListNotesField values after we clonse new list modal.
       */
      if (this.selectedList) {
        this.selectedList = undefined
      }
    }
  }

  public setCategoryNameField = (name: string): void => {
    this.categoryNameField = name
  }

  public setNameListModal = (modal: boolean): void => {
    this.nameListModal = modal
  }

  public setMoveListModal = (modal: boolean): void => {
    this.moveListModal = modal
  }

  public setSelectedList = async (selected: ListNode): Promise<void> => {
    this.selectedList = selected
    if (selected) {
      await this.getListGroupDetails(selected.id)
    }
    if (this.selectedListGroups.length > 0) {
      this.setSelectedGroupValue(0)
    }
    StoreInstances.uiStore.listStoreProps.selectedList = selected
  }

  public setSelectedCategory = (selected: ListNode): void => {
    this.selectedCategory = selected
    StoreInstances.uiStore.listStoreProps.selectedCategory = selected
  }

  public setSelectedGroup = async (selected: ListNode): Promise<void> => {
    this.selectedGroup = selected
    StoreInstances.uiStore.listStoreProps.selectedGroup = selected
    /* @TODO add get products endpoint */

    await this.getListGroupParts()
  }

  public setSelectedGroupValue = (newValue: number): void => {
    this.currentPage = 0
    this.selectedGroupValue = newValue
    StoreInstances.uiStore.listStoreProps.selectedGroupValue = newValue
  }

  public setDeleteSelectedList = (selected: SelectedList): void => {
    this.deleteSelectedList = selected
  }

  public setDeleteSelectedCategory = (selected: SelectedCategory): void => {
    this.deleteSelectedCategory = selected
  }

  public setNameSelectedList = (selected: NameSelectedList): void => {
    this.nameSelectedList = selected
  }

  public setCategoryToRename = (category: IdValuePair<string>): void => {
    this.categoryToRename = category
  }

  public onDeleteList = async (selected: number): Promise<void> => {
    this.deleteListModal = false
    this.deleteSelectedList = null

    try {
      const resp = await ListServiceProvider.deleteList(selected)

      if (resp.status === 200) {
        this.setListDeleted(true)

        const findCategory = (c) => c.id === this.selectedCategory?.id
        const categories = this.root?.getChildren()
        const category = categories?.find(findCategory)
        category.deleteChildById(selected)

        const firstList = category?.getFirstChild()
        this.setSelectedList(firstList)
        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully deleted list'
        )
      }
    } catch (e: unknown) {
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('listDeletionError')
      }
    }
  }

  public onDeleteGroupPart = async (
    deletingPartIds: Array<number>
  ): Promise<void> => {
    const partIdDelete: DeleteGroupParts = {
      partIds: deletingPartIds,
    }
    this.deleteSelectedGroupPart = null

    try {
      const status = await ListServiceProvider.deleteGroupPart(partIdDelete)

      if (status === 200) {
        this.resetMultipleMoveOrDeleteParts()
        this.getListGroupParts()
        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully deleted part'
        )
        if (this.isInSearchMode() !== undefined) {
          this.search(this.searchTerm)
        }
      }
    } catch (e: unknown) {
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('listDeletionError')
      }
    }
  }

  private getFirstNonEmptyCategory(): ListNode {
    const categories = this.root.getChildren()
    const nonEmpty = (c: ListNode) => c.getChildren()?.length !== 0
    return categories.find(nonEmpty)
  }

  public onDeleteCategory = async (selected: number): Promise<void> => {
    const category = this.deleteSelectedCategory
    this.deleteCategoryModal = false
    this.deleteSelectedCategory = null

    try {
      const resp = await ListServiceProvider.deleteCategory(selected)

      if (resp.status !== 200) return

      this.setCategoryDeleted(true)
      StoreInstances.uiStore.displaySuccessNotification(
        'successfullyDeletedCategory'
      )

      this.root.deleteChildById(selected)

      const selectOtherCategory = selected === this.selectedCategory?.id
      if (!selectOtherCategory) return

      const category = this.getFirstNonEmptyCategory()
      if (!category) return

      category.open = true
      this.setSelectedCategory(category)

      const firstList = category.getFirstChild()
      if (firstList) this.setSelectedList(firstList)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (_e: any) {
      const message = 'cannotDeleteCategory'
      const messageParams = [category?.label]
      StoreInstances.uiStore.displayErrorNotification(
        // TODO: refactor the toast notification to accept message parameters because it's too risky right now
        message,
        undefined, // undefined link
        undefined, // undefined time
        messageParams
      )
    }
  }

  public onMoveList = (list: ListNode, targetCategoryId: number): void => {
    this.moveListToAnotherCategory(list, targetCategoryId)
  }

  public moveListToAnotherCategory = async (
    list: ListNode,
    targetCategoryId: number
  ): Promise<void> => {
    try {
      const status = await ListServiceProvider.moveListToAnotherCategory(
        list?.id,
        targetCategoryId
      )

      if (status === 200) {
        StoreInstances.uiStore.displaySuccessNotification(
          'successfullyListMoved'
        )

        this.selectedCategory.deleteChildById(list.id) // Deleting from previous category

        this.moveListModal = false
        this.setListDeleted(true)

        const targetCategory = this.root
          ?.getChildren()
          ?.find((c) => c.id === targetCategoryId)

        targetCategory.addChild(new ListNode(list.id, list.value, 'list'))
        targetCategory.open = true

        const newListNode = targetCategory
          ?.getChildren()
          ?.find((l) => l.id === list.id)

        newListNode.replaceChildren(list.getChildren())

        StoreInstances.uiStore.listStoreProps.selectedCategory = targetCategory
        StoreInstances.uiStore.listStoreProps.selectedList = newListNode

        this.selectedCategory = targetCategory
        this.selectedList = newListNode
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('listNameAlreadyExists')
      } else {
        StoreInstances.uiStore.displayErrorNotification('errorMovingList')
      }
    }
  }

  public onNameList = (listId?: number): void => {
    const categoryId = this.newListCategoryId ?? this.selectedCategory?.id
    const name = this.nameListNameField
    const note = this.nameListNotesField

    if (this.listNameModalType === 'rename') {
      this.updateList(categoryId, listId, name, note)
    } else {
      this.selectedGroup = undefined
      this.selectedListGroups = []

      this.createList(categoryId, name, note)
    }
  }

  public createCategory = async (name: string): Promise<void> => {
    try {
      const { id } = await ListServiceProvider.createCategory(name)
      if (id !== null && id > 0) {
        this.setCategoryNameField('')
        const newCategory = new ListNode(id, name, 'category')
        const currentCategories = this.root.getChildren()
        const categories = [...currentCategories, newCategory]
        const sortedCategories = this.sortCategories(categories)
        this.root?.replaceChildren(sortedCategories)

        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully created category'
        )
      }
      this.categoryNameModal = false
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      const { message }: ListResponseError = (e as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public updateCategory = async (
    name: string,
    categoryId: number
  ): Promise<void> => {
    try {
      const { id } = await ListServiceProvider.updateCategory(name, categoryId)
      if (id === null || id < 0) return

      StoreInstances.uiStore.displaySuccessNotification(
        'Successfully updated category'
      )

      this.setCategoryNameField('')
      const category = this.root?.findById(categoryId)
      category.value = name

      const categories = this.root.getChildren()
      const sortedCategories = categories?.sort((a, b) =>
        a.value.localeCompare(b.value)
      )
      this.root?.replaceChildren(sortedCategories)

      this.categoryNameModal = false
      this.categoryToRename = null
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      const { message }: ListResponseError = (e as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public setNewListCategoryId = (categoryId: number): void => {
    this.newListCategoryId = categoryId
  }

  public createList = async (
    categoryId: number,
    name: string,
    note: string
  ): Promise<void> => {
    const category = this.root?.findById(categoryId)
    const requestData: RequestBodyNewList = {
      name,
      note,
      categoryId,
    }
    try {
      const newList = await ListServiceProvider.createList(requestData)
      if (!newList || newList?.id < 0) return

      this.selectedGroupParts = []
      category.open = true

      const defaultGroup = newList.groups?.[0]
      const newListNode = new ListNode(newList?.id, newList?.name, 'list')
      const groupNode = new ListNode(defaultGroup.id, defaultGroup.name)
      newListNode.addChild(groupNode)
      category.addChild(newListNode)

      const sortedLists = this.sortListsInCategory(category)
      category?.replaceChildren(sortedLists)

      this.selectedGroup = groupNode
      this.selectedList = newListNode
      this.selectedListGroups = [groupNode]

      this.setNameListNameField('')
      this.setNameListNotesField('')
      this.nameListModal = false
      this.nameSelectedList = null
      StoreInstances.uiStore.displaySuccessNotification(
        'successfullyCreatedList'
      )

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 400) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('listNameAlreadyExists')
      }
    }
  }

  public cutText = (text: string): string => {
    const maxLength = 4
    let cutText = text.substr(0, maxLength)

    // Check if the cut text ends with a period
    if (cutText.endsWith('.')) {
      return cutText
    }

    cutText = cutText.trim()

    // Check if there are spaces within the cut text
    const lastSpaceIndex = cutText.lastIndexOf(' ')
    if (lastSpaceIndex !== -1) {
      cutText = cutText.substr(0, lastSpaceIndex)
    }

    return cutText + '...'
  }

  public createListAndGroup = async (
    requestData: {
      name: string
      note: string
      categoryId: number
    },
    groupData: string,
    req: Array<AddPartToList>
  ): Promise<void> => {
    try {
      const responseCreateList =
        await ListServiceProvider.createList(requestData)
      let groupDefaultId = responseCreateList?.groups[0]?.id

      if (responseCreateList.id !== null && responseCreateList.id > 0) {
        this.getCategories()
        this.setNameListNameField('')
        this.setNameListNotesField('')
        this.nameListModal = false
        this.nameSelectedList = null

        if (groupData) {
          const { id: groupId } = await ListServiceProvider.createGroup(
            responseCreateList.id,
            groupData
          )
          groupDefaultId = groupId
        }

        const status = await ListServiceProvider.addPartToGroup(
          responseCreateList.id,
          groupDefaultId,
          req
        )
        if (status === 200 || status === 206) {
          let description = `${req[0]?.partDescription} (${req[0]?.partNumber})`
          const isTablet = window.innerWidth < breakpoints.tablet
          if (isTablet) {
            description = this.cutText(description)
          }
          StoreInstances.uiStore.displaySuccessNotification(
            `${description} was added to list: ${this.selectedList?.value} `,
            {
              text: 'View Lists',
              action: req[0]?.handleListLocation,
            }
          )
          this.getListGroupParts()
          req[0]?.handleClose()
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 400) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }
      if ((e as AxiosError).response.status === 403) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        const responseMessage = message.split(':')
        StoreInstances.uiStore.displayErrorNotification(responseMessage[1])
      }
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification(
          'List name already exists'
        )
      }
    }
  }

  public sortListsInCategory = (category: ListNode): ListNode[] => {
    const lists = category?.getChildren()
    const sortedLists = lists?.sort((a, b) => a.value.localeCompare(b.value))
    return sortedLists
  }

  public updateList = async (
    categoryId: number,
    listId: number,
    name: string,
    note: string
  ): Promise<void> => {
    const requestData: RequestBodyNewList = {
      name,
      note,
    }

    try {
      const { id } = await ListServiceProvider.updateList(listId, requestData)

      if (id === null || id < 0) return

      StoreInstances.uiStore.displaySuccessNotification(
        'successfullyUpdatedList'
      )

      this.selectedList.value = this.nameListNameField
      this.nameListModal = false

      const cat = this.root?.findById(categoryId)
      const sortedLists = this.sortListsInCategory(cat)
      cat?.replaceChildren(sortedLists)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 400) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }

      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('listNameAlreadyExists')
      }
    }
  }

  public createGroup = async (listId: number, name: string): Promise<void> => {
    try {
      const { id } = await ListServiceProvider.createGroup(listId, name)
      if (id !== null && id > 0) {
        this.getListGroupDetails(listId, true)
        this.setShowAddEditGroupModal(false)
        StoreInstances.uiStore.displaySuccessNotification(
          'successfullyCreatedGroup'
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 400) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }
      if ((e as AxiosError).response.status === 403) {
        const { message }: ListResponseError = (e as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }

      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification(
          'groupNameAlreadyExists'
        )
      }
    }
  }

  public updateGroup = async (name: string): Promise<void> => {
    try {
      const { id } = await ListServiceProvider.updateGroup(
        name,
        this.selectedGroup.id
      )
      if (id !== null && id > 0) {
        this.getListGroupDetails(this.selectedList.id, true)
        this.setShowAddEditGroupModal(false)
        StoreInstances.uiStore.displaySuccessNotification(
          'successfullyUpdatedGroup'
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      const { message }: ListResponseError = (e as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public deleteGroup = async (): Promise<void> => {
    try {
      const status = await ListServiceProvider.deleteGroup(
        this.selectedGroup.id
      )

      if (status === 200) {
        this.getListGroupDetails(this.selectedList.id)
        StoreInstances.uiStore.displaySuccessNotification(
          'successfullyDeletedGroup'
        )
        this.setSelectedGroupValue(0)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      const { message }: ListResponseError = (e as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public onNameCategory = (id?: number): void => {
    if (this.categoryNameModalType === 'rename') {
      this.updateCategory(this.categoryNameField, id)
    } else {
      this.createCategory(this.categoryNameField)
    }
  }

  public getListGroupDetails = async (
    listId: number,
    noGroupSelection = false
  ): Promise<void> => {
    this.setGroupPartsLoading(true)
    this.selectedList.deleteAllChildren()
    try {
      const { groups, name, note } = await ListServiceProvider.getListDetails(
        this.selectedCategory.id,
        listId
      )
      this.setNameListNameField(name)
      this.setNameListNotesField(note)
      const groupNodes = groups.map((g) => new ListNode(g.id, g.name))
      this.selectedList.replaceChildren(groupNodes)
      this.selectedListGroups = groupNodes
      StoreInstances.uiStore.listStoreProps.selectedListGroups = groupNodes
      if (!noGroupSelection) {
        await this.setSelectedGroup(groupNodes?.[0])
      }
      this.setGroupPartsLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      StoreInstances.uiStore.displayErrorNotification(JSON.stringify(e.message))
    }
  }

  public getListGroups = async (
    categoryId: number,
    listId: number
  ): Promise<void> => {
    try {
      const { groups } = await ListServiceProvider.getListDetails(
        categoryId,
        listId
      )
      this.moveListGroups = groups
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      StoreInstances.uiStore.displayErrorNotification(JSON.stringify(e.message))
    }
  }

  public onMoveGroupParts = async (
    targetGroupId: number,
    movingGroupPartIds: Array<number>
  ): Promise<void> => {
    const movingGroupParts: MoveGroupParts = {
      partIds: movingGroupPartIds,
    }

    try {
      const status = await ListServiceProvider.movePartsToAnotherGroup(
        targetGroupId,
        movingGroupParts
      )

      if (status === 200) {
        this.resetMultipleMoveOrDeleteParts()
        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully parts have been moved'
        )
        if (this.isInSearchMode() !== undefined) {
          this.search(this.searchTerm)
          return
        }
        this.getListGroupDetails(this.selectedList.id)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      if ((e as AxiosError).response.status === 409) {
        StoreInstances.uiStore.displayErrorNotification('Error moving parts')
      }
    }
  }

  public sortCategories = (categories: ListNode[]): ListNode[] => {
    // default categories to display at top of the list (MPV3-7532)
    const defaultCategories: ListNode[] = categories.filter(
      (c) => c.type === 'category' && categoriestoExclude.includes(c.value)
    )

    const moveToBottom = (a, b, name) => {
      if (b.value === name) return -1
      if (a.value === name) return 1
      return 0
    }

    const sortedCategories = categories
      .filter((item) => !categoriestoExclude.includes(item.value))
      ?.sort((a, b) => a.value.localeCompare(b.value))
      ?.sort((a, b) => moveToBottom(a, b, SELLER_MANAGED_LIST_LABEL))

    return [...defaultCategories, ...sortedCategories]
  }

  public getCategories = async (isListUpdated?: boolean): Promise<void> => {
    /* GET for the side panel */
    this.loadingCategories = true
    const { categories } =
      await ListServiceProvider.getCategoriesAndLists() /* @TODO: get categories list */

    const categoryNodes = categories.map((c) => {
      const catNode = new ListNode(c.id, c.name, 'category')
      catNode.open = catNode?.id === this.selectedCategory?.id
      const listNodes = c.lists?.map((l) => new ListNode(l.id, l.name, 'list'))
      catNode.replaceChildren(listNodes)
      return catNode
    })

    const sortedCategories = this.sortCategories(categoryNodes)
    this.root?.replaceChildren(sortedCategories)

    const firstNonEmptyCategory: ListNode = sortedCategories.find(
      (c) => c.getChildren().length !== 0
    )
    if (!this.selectedCategory && firstNonEmptyCategory) {
      firstNonEmptyCategory.open = true
      this.setSelectedCategory(firstNonEmptyCategory)
    }

    const firstList = firstNonEmptyCategory?.getFirstChild()
    if (!this.selectedList && firstList) this.setSelectedList(firstList)
    else this.setSelectedList(this.selectedList)

    // this is required to re-render updated list label and also to maintain selected category and list as per (MP4P-954)
    if (isListUpdated) {
      const upDatedList = categories
        ?.find((item) => item.id === this.selectedCategory?.id)
        ?.lists.find((item) => item.id === this.selectedList?.id)
      const updatedListNode = new ListNode(
        upDatedList.id,
        upDatedList.name,
        'list'
      )
      this.setSelectedList(updatedListNode)
    }

    if (this.onCategoryDeleted || this.onListDeleted) {
      this.setSelectedCategory(firstNonEmptyCategory)
      this.setSelectedList(firstList)
    }

    this.loadingCategories = false
  }

  public setCategoryDeleted = (value: boolean): void => {
    this.onCategoryDeleted = value
  }

  public setListDeleted = (value: boolean): void => {
    this.onListDeleted = value
  }

  public getSelectedCategoryLabel = (): string => {
    return this.selectedCategory?.value
  }

  public getAllSelectedCategoryLabels = (): string[] => {
    return this._selectedPartsBasket?.items?.map((i) => i.category?.value)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
  public getSelectedCategoryItems = (): any => {
    return this.selectedCategory?.getChildren()
  }

  public getSelectedListId = (): number => {
    return this.selectedList?.id
  }

  public getSelectedListLabel = (): string => {
    return this.selectedList?.value
  }

  private prepareSortData = (): string => {
    return this.sortOrder
      .filter((item) => item && item.property)
      .map((item) => `${item.property}_${item.dir === 'asc' ? 0 : 1}`)
      .join()
  }

  public async getAllPartsInGroup(
    listId: number,
    groupId: number,
    start: number = 0,
    limit: number = 1000
  ): Promise<GroupPartResponse> {
    const fragment = await ListServiceProvider.getGroupDetails(
      listId,
      groupId,
      {
        start,
        limit,
        sortBy: this.prepareSortData(),
      }
    )

    const end = start + fragment?.items?.length
    const loadOtherFragment = end < fragment.totalCount

    if (loadOtherFragment) {
      const otherFragment = await this.getAllPartsInGroup(
        listId,
        groupId,
        end,
        limit
      )
      const newItems = otherFragment?.items || []
      fragment.items?.push(...newItems)
    }

    return fragment
  }

  public getListGroupParts = async (): Promise<void> => {
    try {
      if (!this.selectedList?.id || !this.selectedGroup?.id) {
        return
      }
      this.setGroupPartsLoading(true)

      const response = await this.getAllPartsInGroup(
        this.selectedList?.id,
        this.selectedGroup?.id
      )

      const { items, totalCount } = response
      this.selectedGroupParts = items ?? []
      this.selectedGroupPartsCount = totalCount ?? 0
      this.setGroupPartsLoading(false)
    } catch (error) {
      this.setGroupPartsLoading(false)
      const isEmpty = Object.entries(error).length === 0
      if (isEmpty) {
        this.selectedGroupParts = []
        this.selectedGroupPartsCount = 0
      } else {
        const { message }: ListResponseError = (error as AxiosError).response
          .data as ListResponseError
        StoreInstances.uiStore.displayErrorNotification(message)
      }
    }
    this.setCategoryDeleted(false)
    this.setListDeleted(false)
  }

  public isInSearchMode = (): boolean => {
    return this.searchTerm
  }

  public getMultiSearchParts = (): (Product & {
    isSelected: boolean
  })[] => {
    const allParts = []
    this.matchingFilteredSearchTerms.forEach((list) => {
      list.groups.forEach((group) => {
        group.items.forEach((part) => {
          allParts.push(part)
        })
      })
    })
    return allParts
  }

  private setGroupPartsLoading = (isLoading: boolean) => {
    this.groupPartsLoading = isLoading
  }

  public addPartToGroup = async (req: Array<AddGroupPart>): Promise<void> => {
    try {
      const status = await ListServiceProvider.addPartToGroup(
        this.selectedList.id,
        this.selectedGroup.id,
        req
      )
      if (status === 200 || status === 206) {
        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully added item to group'
        )
        this.getListGroupParts()
      }
    } catch (error) {
      const { message }: ListResponseError = (error as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public addPartToList = async (req: Array<AddPartToList>): Promise<void> => {
    try {
      const status = await ListServiceProvider.addPartToGroup(
        this.selectedList.id,
        this.selectedGroup.id,
        req
      )
      if (status === 200 || status === 206) {
        let description = `${req[0]?.partDescription} (${req[0]?.partNumber})`
        const isTablet = window.innerWidth < breakpoints.tablet
        if (isTablet) {
          description = this.cutText(description)
        }
        StoreInstances.uiStore.displaySuccessNotification(
          `${description} was added to list: ${this.selectedList?.value} `,
          {
            text: 'View Lists',
            action: req[0]?.handleListLocation,
          }
        )

        this.getListGroupParts()
        req[0]?.handleClose()
      }
    } catch (error) {
      const { message }: ListResponseError = (error as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
    }
  }

  public updatePartInGroup = async (
    req: Array<UpdateGroupPart>
  ): Promise<void> => {
    try {
      const status = await ListServiceProvider.updatePartInGroup(
        this.selectedList.id,
        this.selectedGroup.id,
        req
      )
      if (status === 200 || status === 206) {
        StoreInstances.uiStore.displaySuccessNotification(
          'Successfully updated part'
        )
        this.getListGroupParts()
      }
    } catch (error) {
      const { message }: ListResponseError = (error as AxiosError).response
        .data as ListResponseError
      StoreInstances.uiStore.displayErrorNotification(message)
      throw error
    }
  }

  public sortGroupParts = (sortList: Array<GroupPart>): void => {
    this.selectedGroupParts = sortList
  }

  public selectAllPartsFromListOfResults = (
    category: IdValueGeneric<number, string>,
    listId: number,
    isSelected: boolean
  ): void => {
    const list = this.matchingFilteredSearchTerms.find(
      (list) => list.id === listId
    )

    const groups = list?.groups

    groups?.forEach((g) => {
      const itemsToAdd = isSelected ? g.items : []
      this.addItemsToBasket(category, listId, g.id, itemsToAdd)
    })
  }

  public countAllPartsInAList = (listId: number): number => {
    let count = 0
    this.matchingFilteredSearchTerms.forEach((list) => {
      if (list.id === listId) {
        list.groups.forEach((group) => {
          group.items.forEach(() => {
            count += 1
          })
        })
      }
    })
    return count
  }

  public resetMultipleMoveOrDeleteParts = (): void => {
    this.multipleMoveOrDeleteParts = []
  }

  public getMultipleMoveOrDeletePartsCount = (): number => {
    let totalPartsCount = 0

    this.multipleMoveOrDeleteParts.forEach((category) => {
      category.groups.forEach((group) => {
        totalPartsCount += group.parts.length
      })
    })

    return totalPartsCount
  }

  private updateGroupPart = (
    id: number,
    payload: IUpdateGroupPartPaylod
  ): GroupPart => {
    let updatedPart: GroupPart
    if (this.searchTerm) {
      const temp = this.matchingFilteredSearchTerms.map((list) => {
        return {
          ...list,
          groups: list.groups.map((group) => {
            return {
              ...group,
              items: group.items.map((part) => {
                if (part.id === id) {
                  updatedPart = { ...part, ...payload }
                  return updatedPart
                }
                return part
              }),
            }
          }),
        }
      })
      this.matchingFilteredSearchTerms = temp
    } else {
      const temp = this.selectedGroupParts.map((part) => {
        if (part.id === id) {
          updatedPart = { ...part, ...payload }
          return updatedPart
        }
        return part
      })
      this.selectedGroupParts = temp
    }
    return updatedPart
  }

  public updatePartQty = (stockQty: number, id: number): void => {
    const updatedPart = this.updateGroupPart(id, { stockQty })

    // MPV3-7633 updating selected part stock qty after changing the value in part row
    this._selectedPartsBasket.items = this._selectedPartsBasket.items.map(
      (item) => ({
        ...item,
        groups: item.groups.map((group) => ({
          ...group,
          parts: group.parts.map((part) => ({
            ...part,
            stockQty: part.id === id ? updatedPart.stockQty : part.stockQty,
          })),
        })),
      })
    )
  }

  public sortListParts = (sortList: Array<CheckedListParts>): void => {
    this.checkedListParts = sortList
  }

  setImportListPartsLoading = (loading: boolean): void => {
    this.importListPartsLoading = loading
  }

  public importPartsList = async (
    listId: string,
    fileToImport: File
  ): Promise<ImportStatus> => {
    try {
      this.setImportListPartsLoading(true)
      this.listImportRespData = await ListServiceProvider.importListParts(
        listId,
        fileToImport
      )
      const queueId = this.listImportRespData?.queueId
      return await this.monitorQueueStatus(queueId)
    } catch (_e) {
      this.listImportRespData = {
        queueId: '',
      }
      return ImportStatus.FAILED
    } finally {
      this.setImportListPartsLoading(false)
    }
  }

  public monitorQueueStatus = async (
    queueId: string,
    counter = 0
  ): Promise<ImportStatus> => {
    const { STARTED, PROCESSING, FAILED } = ImportStatus

    await new Promise((resolve) => setTimeout(resolve, this._retryInterval))

    counter++
    if (counter > this._retryLimit) {
      StoreInstances.uiStore.displayErrorNotification('listImportTimeOutMsg')
      return FAILED
    }

    const status = await this.checkImportStatus(queueId)
    if (status == STARTED || status == PROCESSING)
      return this.monitorQueueStatus(queueId, counter)
    return status
  }

  public checkImportStatus = async (queueId: string): Promise<ImportStatus> => {
    const { PROCESSING, FAILED, SUCCESS } = ImportStatus

    const resp = await ListServiceProvider.getListImportDetails(queueId)

    if (!resp) return PROCESSING //  Assume processing due to observed API behavior

    const error = resp?.find(({ status }) => status === FAILED)
    if (error) {
      const message = error?.message
      StoreInstances.uiStore.displayErrorNotification(message)
      return FAILED
    }

    const success = resp?.find(({ status }) => status === SUCCESS)
    if (success) {
      this.listImportDetails = success
      this.getListGroupDetails(this.selectedList.id)
      ListServiceProvider.deleteListQueue(queueId)
      return SUCCESS
    }

    return PROCESSING
  }

  private isAutoLocationChangeEnabled = (): boolean => {
    return (
      StoreInstances.userStore?.preferences?.findit_orderIfNotAvail === 'true'
    )
  }

  public setChangeLocationParts = (
    changeLocationParts: Array<ProductLocationModel>
  ): void => {
    this.changeLocationParts = changeLocationParts
  }

  public setShowAddEditGroupModal = (value: boolean): void => {
    this.showAddEditGroupModal = value
  }

  public getListByIdAndGroupId = async (
    listId: number,
    groupId: number
  ): Promise<GroupPartResponse> => {
    const groupDetails = await ListServiceProvider.getGroupDetails(
      listId,
      groupId,
      {
        start: 0,
        limit: 1000,
        sortBy: this.prepareSortData(),
      }
    )
    return groupDetails
  }

  public toggleCategory = (category: ListNode): void => {
    const cat = this.root?.getChildren()?.find((c) => c.id === category.id)
    cat.open = !cat?.open
  }

  public resetSelectedListGroups = (): void => {
    this.selectedListGroups = []
  }

  public handleAddToCart = (listParts: ValidateListPart[]): void => {
    const { cart, uiStore } = StoreInstances
    cart.addVehicleToCart(this.selectedVehicle)

    const allParts = this.getAllSelectedParts()

    // bug/MPV3-3790 parts with min quantity should be updated in the cart.
    let hasPartQtyChanged = false
    try {
      const cartParts = listParts.map((part) => {
        const stockQty = allParts.find((p) => p.id === part.id).stockQty
        const minQty = part.location?.find((loc) => loc.isSelected).minQty

        const hasMinQuantity = stockQty >= minQty

        const partQuantity = hasMinQuantity ? stockQty : minQty

        if (!hasMinQuantity) {
          hasPartQtyChanged = !hasMinQuantity
        }

        return {
          product: part,
          locationId: part.location?.[0]?.locationId,
          quantity: partQuantity,
          vehicle: this.selectedVehicle,
          autoLocationChange: this.isAutoLocationChangeEnabled(),
        }
      })

      cart
        .setQtyAtLocationBulk(cartParts, false)
        .then(() => {
          const shoppingCartVehicle = cart.findCartVehicle(this.selectedVehicle)
          if (hasPartQtyChanged)
            uiStore.displaySuccessNotification(`quantityRequestedHasChanged`)
          showCartPartComparisionNotification(
            listParts,
            shoppingCartVehicle,
            [],
            [],
            {
              text: 'view cart', // TODO: Make trannslations work outside react components.
              action: () => {
                history.push('/cart')
              },
            }
          )
          this.clearAllSelections()
          this.setListPartsToCartLoading(false)
        })
        .catch((e) => {
          uiStore.displayErrorNotification(e.message)
        })
        .finally(() => {
          this.setListPartsToCartLoading(false)
        })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      uiStore.displayErrorNotification(e.message)
    }
  }

  public validateAndAddToCart = async (
    req: ValidatePartsRequest
  ): Promise<void> => {
    try {
      const validatedListParts = await getValidatedPartsList(req).finally(() =>
        this.setListPartsToCartLoading(false)
      )
      return this.handleAddToCart(validatedListParts || [])
    } catch (e) {
      StoreInstances.uiStore.displayErrorNotification(
        `No Part was added to the cart`
      )
      throw e
    }
  }

  public addListPartsToCart = (): void => {
    this.setListPartsToCartLoading(true)

    const partsToValidate = this.getAllSelectedParts()

    // removed error validating list error,as we display other error notification when we add a part with no linecode to cart.
    this.validateAndAddToCart({
      partIds: partsToValidate.map((part) => part.id) || [],
    })
  }

  public setListPartsToCartLoading = (value: boolean): void => {
    this.arePartsBeingAddedToCart = value
  }

  public selectItems(
    category: IdValueGeneric<number, string>,
    listId: number,
    groupId: number,
    start?: number,
    end?: number
  ): void {
    const all = !start && !end
    const parts = all
      ? this.selectedGroupParts
      : this.selectedGroupParts.slice(start, end)

    this.addItemsToBasket(category, listId, groupId, parts)
  }

  public clearAllSelections(): void {
    this._selectedPartsBasket = null
  }

  public selectItem(
    category: IdValueGeneric<number, string>,
    listId: number,
    groupId: number,
    part: GroupPart
  ): void {
    const selected = !this.isPartSelected(listId, groupId, part)
    if (selected) {
      this.addItemToBasket(category, listId, groupId, part)
    } else {
      this.removeItemFromBasket(listId, groupId, part)
    }
  }

  public addItemsToBasket(
    category: IdValueGeneric<number, string>,
    listId: number,
    groupId: number,
    parts: GroupPart[]
  ): void {
    const findList = (i) => i.listId === listId
    const filterGroups = (g) => g.id !== groupId
    const filterLists = (i) => i.listId !== listId

    const foundList = this._selectedPartsBasket?.items?.find(findList)
    const list = { category, listId, groups: foundList?.groups || [] }

    const groups = list?.groups?.filter(filterGroups)

    const currentSelectedParts =
      foundList?.groups.find((g) => g.id === groupId)?.parts || []

    const currentSelectedIds = new Set(currentSelectedParts.map((cS) => cS.id))

    const partsToAdd = parts.filter((p) => !currentSelectedIds.has(p.id))

    const totalParts = [...currentSelectedParts, ...partsToAdd]

    groups.push({ id: groupId, parts: totalParts })

    list.groups = groups

    const lists = this._selectedPartsBasket?.items?.filter(filterLists) || []
    const items = [...lists, list]
    this._selectedPartsBasket = { items }
  }

  public removeItemFromBasket(
    listId: number,
    groupId: number,
    part: GroupPart
  ): void {
    const list = this._selectedPartsBasket?.items?.find(
      (i) => i.listId === listId
    )
    if (!list) return

    const group = list.groups?.find((g) => g.id === groupId)
    if (!group) return

    group.parts = group.parts?.filter((p) => p.id !== part.id)

    if (group.parts.length <= 0) {
      list.groups = list.groups.filter((g) => g.id !== group.id)
    }

    if (list.groups.length <= 0) {
      this._selectedPartsBasket.items = this._selectedPartsBasket.items.filter(
        (l) => l.listId !== list.listId
      )
    }
  }

  public addItemToBasket(
    category: IdValueGeneric<number, string>,
    listId: number,
    groupId: number,
    part: GroupPart
  ): void {
    const findList = (i) => i.listId === listId
    const filterGroups = (g) => g.id !== groupId
    const filterLists = (i) => i.listId !== listId

    const foundList = this._selectedPartsBasket?.items?.find(findList)
    const list = { category, listId, groups: foundList?.groups || [] }

    const group = list.groups.find((g) => g.id === groupId)
    const existingParts = group?.parts ?? []

    const groups = list?.groups?.filter(filterGroups)
    groups.push({ id: groupId, parts: [...existingParts, part] })
    list.groups = groups

    const lists = this._selectedPartsBasket?.items?.filter(filterLists) || []
    const items = [...lists, list]
    this._selectedPartsBasket = { items }
  }

  public isPartSelected(
    listId: number,
    groupId: number,
    part: GroupPart
  ): boolean {
    const findList = (i) => i.listId === listId
    const list = this._selectedPartsBasket?.items?.find(findList)
    if (!list) return false

    const group = list?.groups?.find((g) => g.id === groupId)
    if (!group) return false

    return group.parts?.some((p) => p.id === part.id) ?? false
  }

  public getAllSelectedParts(): GroupPart[] {
    const groups = this._selectedPartsBasket?.items?.flatMap((i) => i.groups)
    return groups?.flatMap((g) => g.parts)
  }

  public getNumOfSelectedLists(): number {
    const lists = this._selectedPartsBasket?.items?.filter((i) =>
      i.groups?.some((g) => g.parts?.length > 0)
    )
    return lists?.length ?? 0
  }

  public getNumOfSelectedGroups(): number {
    const groups = this._selectedPartsBasket?.items
      ?.flatMap((i) => i.groups)
      ?.filter((g) => g.parts?.length > 0)
    return groups?.length ?? 0
  }

  public getSelectedPartsBasket(): SelectedPartsBasket {
    return this._selectedPartsBasket
  }

  public updateLastUsed = (): void => {
    this._selectedPartsBasket?.items?.forEach((selectedList) => {
      const listId = selectedList.listId
      const partIds = selectedList.groups.flatMap((g) =>
        g.parts.flatMap((p) => p.id)
      )
      ListServiceProvider.updateLastUsed(listId, partIds)
    })
  }
}

export const ListsStoreContext = createContext<ListsStore>(undefined)

export const useListsStore = (): ListsStore => {
  return useContext(ListsStoreContext)
}
